import type { MangoApiClient } from '@mangovoice/mango-api-client'
import type {
  PaginatedApiResult,
  PatchExtensionWebhookConfigRequest,
  PatchWebhookConfigRequest,
  WebhookAuthKeys,
  WebhookConfigDetail,
  WebhookConfigListItem,
} from '@mangovoice/types'

import { WEBHOOKS_ENDPOINTS } from './endpoints'
import type { ApiResponse } from './types'

export type WebhooksApiClient = MangoApiClient['webhooks']

export const createWebhooksApi = (client: WebhooksApiClient) => ({
  listWebhooks: (
    pbxId: number,
  ): Promise<ApiResponse<PaginatedApiResult<WebhookConfigListItem>>> =>
    client.get<PaginatedApiResult<WebhookConfigListItem>>(
      `${WEBHOOKS_ENDPOINTS.LIST}?pbx_id=${pbxId}`,
    ),

  getWebhookDetail: (
    pbxId: number,
    integrationId: number,
  ): Promise<ApiResponse<WebhookConfigDetail>> =>
    client.get<WebhookConfigDetail>(
      `${WEBHOOKS_ENDPOINTS.DETAIL(integrationId)}?pbx_id=${pbxId}`,
    ),

  patchWebhook: (
    integrationId: number,
    data: PatchWebhookConfigRequest,
  ): Promise<ApiResponse<WebhookConfigDetail>> =>
    client.patch<WebhookConfigDetail>(
      WEBHOOKS_ENDPOINTS.DETAIL(integrationId),
      data,
    ),

  patchExtensionWebhook: (
    integrationId: number,
    extId: number,
    data: PatchExtensionWebhookConfigRequest,
  ): Promise<ApiResponse<unknown>> =>
    client.patch<unknown>(
      WEBHOOKS_ENDPOINTS.EXTENSION_PATCH(integrationId, extId),
      data,
    ),

  getAuthKeys: (pbxId: number): Promise<ApiResponse<WebhookAuthKeys>> =>
    client.get<WebhookAuthKeys>(WEBHOOKS_ENDPOINTS.AUTH_KEYS(pbxId)),

  generateAuthKeys: (pbxId: number): Promise<ApiResponse<WebhookAuthKeys>> =>
    client.post<WebhookAuthKeys>(WEBHOOKS_ENDPOINTS.AUTH_KEYS_GENERATE(pbxId)),

  regenerateAuthKeys: (pbxId: number): Promise<ApiResponse<WebhookAuthKeys>> =>
    client.post<WebhookAuthKeys>(
      WEBHOOKS_ENDPOINTS.AUTH_KEYS_REGENERATE(pbxId),
    ),

  deleteAuthKeys: (pbxId: number): Promise<ApiResponse<undefined>> =>
    client.delete<undefined>(WEBHOOKS_ENDPOINTS.AUTH_KEYS(pbxId)),
})

export type WebhooksApi = ReturnType<typeof createWebhooksApi>
